/**
 * Rattacher une graine 2FA importee seule a l'identifiant DEJA au coffre pour le meme compte, au lieu d'une entree a
 * part : un authentificateur (Aegis, 2FAS, Google Authenticator) n'exporte que « emetteur + compte + graine », et
 * l'utilisateur qui a deja `google.com` / `ana@example.eu` dans le coffre veut le code SUR cette fiche, pas a cote.
 *
 * Rattachement seulement s'il est SUR : l'entree importee ne porte qu'une graine (ni mot de passe, ni adresse), le
 * compte est identique (a la casse pres) et non vide, l'emetteur designe le site de l'entree (son nom, ou le domaine
 * enregistrable d'une de ses adresses : « Google » ↔ `accounts.google.com`), l'entree n'a pas deja de graine, et UN
 * SEUL identifiant repond. Un emetteur dont le nom ne recoupe pas l'adresse de la fiche (« Microsoft » ↔ `live.com`,
 * « YouTube » ↔ `accounts.google.com`) la designe par les domaines equivalents SOURCES (`equivalentDomainsOf`, meme
 * compte chez l'editeur), sauf si la fiche s'en est retiree (`equivalentDomains === false`). Sinon `null` : l'import cree une entree a part, comme avant -- jamais une graine posee sur
 * le mauvais compte. Corbeille et archives ne recoivent rien. Module pur ; la reunion sans perte (notes, etiquettes,
 * champs) est celle de la fusion de doublons (`mergeDuplicateSecrets`).
 */
import type { VaultItem } from '@klavox/vault-model';
import { equivalentDomainsOf, registrableDomain, siteHostname } from '@klavox/vault-model';
import type { ImportedEntry } from './csv-import.js';
import { isArchived } from './entry-archive.js';
import { isTrashed } from './entry-trash.js';

/** « Société Générale » / `societegenerale.fr` → `societegenerale` : lettres et chiffres, sans accent ni casse. */
function compact(value: string): string {
  return value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]/gu, '');
}

/** Cle de site d'un nom d'emetteur ou d'entree : le libelle avant le suffixe public s'il s'ecrit en domaine. */
function siteKey(name: string): string {
  const host = /^[^\s/]+\.[^\s/]+$/u.test(name.trim()) ? siteHostname(name) : null;
  const domain = host === null ? null : registrableDomain(host);
  return compact(domain === null ? name : domain.split('.')[0]!);
}

/** Cles de site des adresses de l'entree, et de leurs domaines equivalents sauf opt-out (comme `entryMatchesSite`). */
function uriKeys(uris: readonly string[], equivalents: boolean): string[] {
  return uris.flatMap((uri) => {
    const host = siteHostname(uri);
    const domain = host === null ? null : registrableDomain(host);
    if (domain === null) return [];
    const others = equivalents ? equivalentDomainsOf(uri) : [];
    return [domain, ...others].map((site) => compact(site.split('.')[0]!));
  });
}

/**
 * Graine seule : ce qu'exporte un authentificateur, et rien qu'une fiche existante ne sache deja recevoir. Une graine
 * que l'authentificateur avait mise a la corbeille (Ente Auth, importee aux archives) n'est jamais posee sur une fiche
 * active : elle est souvent revoquee, et ses codes seraient faux.
 */
export function isLoneSeed(entry: ImportedEntry): boolean {
  return (
    entry.type === 'login' &&
    entry.archived !== true &&
    entry.totpSecret.length > 0 &&
    entry.password.length === 0 &&
    entry.uris.length === 0 &&
    entry.username.trim().length > 0 &&
    (entry.attachments ?? []).length === 0 &&
    (entry.passkeys ?? []).length === 0 &&
    entry.sshKey === undefined
  );
}

/** Identifiant unique qui recoit la graine importee, `null` s'il n'y en a pas exactement un. */
export function seedAttachTarget(entry: ImportedEntry, items: readonly VaultItem[]): VaultItem | null {
  if (!isLoneSeed(entry)) return null;
  const issuer = siteKey(entry.name);
  if (issuer.length === 0) return null;
  const account = entry.username.trim().toLowerCase();
  const candidates = items.filter((item) => {
    const secrets = item.secrets;
    if (item.type !== 'login' || item.deletedAt !== null || isTrashed(secrets) || isArchived(secrets)) return false;
    if (typeof secrets.totpSecret === 'string' && secrets.totpSecret.length > 0) return false;
    return (secrets.username ?? '').trim().toLowerCase() === account;
  });
  const direct = candidates.filter(
    (item) => siteKey(item.secrets.name ?? '') === issuer || uriKeys(item.secrets.uris ?? [], false).includes(issuer),
  );
  // Domaines equivalents en repli seulement : la fiche « Google » passe avant la fiche « YouTube » du meme compte.
  const matches =
    direct.length > 0
      ? direct
      : candidates.filter((item) => item.secrets.equivalentDomains !== false && uriKeys(item.secrets.uris ?? [], true).includes(issuer));
  return matches.length === 1 ? matches[0]! : null;
}
